import MapboxGlDraw from '@mapbox/mapbox-gl-draw';
import {
    type MapboxMap,
    MapItemType,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    GeoBounds,
    GeoCoordinate,
} from '@polaris-customerweb/sharedcomponents.shared';
import type { LngLatBounds, MapEvent, MapMouseEvent } from 'mapbox-gl';
import {
    BehaviorSubject,
    EMPTY,
    fromEvent,
    Observable,
    Subject,
    timer,
} from 'rxjs';
import { filter, map, startWith, switchMap, take } from 'rxjs/operators';
import { MapExtents } from 'src/app/state/map/map.reducer';

export class MapDisplay {
    map: MapboxMap;
    draw: MapboxGlDraw;
    shouldRouteOnMapClick = true;

    private subjects: (Subject<unknown> | BehaviorSubject<unknown>)[] = [];

    // create and update behavior subjects immediately so their value is available on first access
    // behavior subjects
    private _mapExtentsSubject: BehaviorSubject<MapExtents>;
    private _mapExtents$: Observable<MapExtents>;
    private _mapLoadedSubject$ = new BehaviorSubject<MapboxMap | undefined>(
        undefined
    );
    private _mapLoaded$: Observable<MapboxMap>;
    private _mapSourcesLoadedSubject = new BehaviorSubject<
        MapboxMap | undefined
    >(undefined);
    private _mapSourcesLoaded$: Observable<MapboxMap>;
    private _requestedBoundsSubject = new BehaviorSubject<
        GeoBounds | undefined
    >(undefined);
    private _requestedBounds$: Observable<GeoBounds | undefined>;
    private _requestedCenterSubject = new BehaviorSubject<
        GeoCoordinate | undefined
    >(undefined);
    private _requestedCenter$: Observable<GeoCoordinate | undefined>;
    private _zoomOverrideSubject = new BehaviorSubject<number | undefined>(
        undefined
    );
    private _zoomOverride$: Observable<number | undefined>;

    // subjects
    private _mapItemClickSubject = new Subject<{
        itemType: MapItemType;
        id?: string;
    }>();
    private _mapItemClick$: Observable<{ itemType: MapItemType; id?: string }>;
    private _mapItemHoverSubject = new Subject<{
        itemType: MapItemType;
        id?: string;
    }>();
    private _mapItemHover$: Observable<{ itemType: MapItemType; id?: string }>;

    // observables
    private _mapClick$: Observable<MapMouseEvent>;
    private _mapDoubleClick$: Observable<MapMouseEvent>;
    private _mapDragEnd$: Observable<MapEvent>;
    private _mapDragStart$: Observable<MapEvent>;
    private _mapMouseDown$: Observable<MapMouseEvent>;
    private _mapMouseMove$: Observable<MapMouseEvent>;
    private _mapMouseUp$: Observable<MapMouseEvent>;
    private _mapStyleChanged$: Observable<MapboxMap>;
    private _mapZoomEnd$: Observable<MapEvent>;
    private _mapZoomStart$: Observable<MapEvent>;

    private isDestroyed = false;

    constructor(mapboxMap: MapboxMap) {
        this.map = mapboxMap;

        this._mapExtentsSubject = new BehaviorSubject(
            this.getExtentsFromMap(mapboxMap)
        );

        this.subjects.push(
            this._mapExtentsSubject,
            this._mapSourcesLoadedSubject,
            this._requestedBoundsSubject,
            this._requestedCenterSubject,
            this._zoomOverrideSubject
        );
    }

    //#region Behavior subject as observable getters
    /**
     * @description - Observable that emits map extents when map is zoomed or panned.
     */
    get mapExtents$(): Observable<MapExtents> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapExtents$) {
            this.map.on('moveend', (event) => {
                this._mapExtentsSubject.next(
                    this.getExtentsFromMap(event.target)
                );
            });
            this._mapExtents$ = this._mapExtentsSubject.asObservable();
        }

        return this._mapExtents$;
    }

    /**
     * @description - Observable that emits the underlying map object once the map successfully loads. After the map
     * successfully loads the first time, this observable will immediately emit the map object when subscribed.
     */
    get mapLoaded$(): Observable<MapboxMap> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapLoaded$) {
            // Fire once when map loaded event fires.
            // This will have to be set in the map setter if the map param in the constructor becomes optional.
            this.map.once('load', (event) => {
                this._mapLoadedSubject$.next(event.target);
            });

            this._mapLoaded$ = this._mapLoadedSubject$
                .asObservable()
                .pipe(
                    filter((mapboxMap) => Boolean(mapboxMap))
                ) as Observable<MapboxMap>;
        }

        return this._mapLoaded$;
    }

    /**
     * @description - Observable that emits the underlying map object whenever map sources are loaded. Sources are
     * loaded every time the underlying map changes (e.g. to/from satellite).
     */
    get mapSourcesLoaded$(): Observable<MapboxMap> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapSourcesLoaded$) {
            this._mapSourcesLoaded$ = this._mapSourcesLoadedSubject
                .asObservable()
                .pipe(
                    filter((mapboxMap) => Boolean(mapboxMap))
                ) as Observable<MapboxMap>;
        }

        return this._mapSourcesLoaded$;
    }

    get requestedBounds$(): Observable<GeoBounds | undefined> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._requestedBounds$) {
            this._requestedBounds$ =
                this._requestedBoundsSubject.asObservable();
        }

        return this._requestedBounds$;
    }

    get requestedCenter$(): Observable<GeoCoordinate | undefined> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._requestedCenter$) {
            this._requestedCenter$ =
                this._requestedCenterSubject.asObservable();
        }

        return this._requestedCenter$;
    }

    get zoomOverride$(): Observable<number | undefined> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._zoomOverride$) {
            this._zoomOverride$ = this._zoomOverrideSubject.asObservable();
        }

        return this._zoomOverride$;
    }
    // #endregion

    // #region Subject as observable getters
    get mapItemClick$(): Observable<{
        itemType: MapItemType;
        id?: string;
    }> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapItemClick$) {
            this._mapItemClick$ = this._mapItemClickSubject.asObservable();
        }

        return this._mapItemClick$;
    }

    get mapItemHover$(): Observable<{
        itemType: MapItemType;
        id?: string;
    }> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapItemHover$) {
            this._mapItemHover$ = this._mapItemHoverSubject.asObservable();
        }

        return this._mapItemHover$;
    }
    // #endregion

    // #region Map event as observable getters
    get mapClick$(): Observable<MapMouseEvent> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapClick$) {
            this._mapClick$ = fromEvent(this.map, 'click');
        }

        return this._mapClick$;
    }

    get mapDoubleClick$(): Observable<MapMouseEvent> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapDoubleClick$) {
            this._mapDoubleClick$ = fromEvent(this.map, 'dblclick');
        }

        return this._mapDoubleClick$;
    }

    get mapDragEnd$(): Observable<MapEvent> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapDragEnd$) {
            this._mapDragEnd$ = fromEvent(this.map, 'dragend');
        }

        return this._mapDragEnd$;
    }

    get mapDragStart$(): Observable<MapEvent> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapDragStart$) {
            this._mapDragStart$ = fromEvent(this.map, 'dragstart');
        }

        return this._mapDragStart$;
    }

    get mapMouseDown$(): Observable<MapMouseEvent> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapMouseDown$) {
            this._mapMouseDown$ = fromEvent(this.map, 'mousedown');
        }

        return this._mapMouseDown$;
    }

    get mapMouseMove$(): Observable<MapMouseEvent> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapMouseMove$) {
            this._mapMouseMove$ = fromEvent(this.map, 'mousemove');
        }

        return this._mapMouseMove$;
    }

    get mapMouseUp$(): Observable<MapMouseEvent> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapMouseUp$) {
            this._mapMouseUp$ = fromEvent(this.map, 'mouseup');
        }

        return this._mapMouseUp$;
    }

    /**
     * @description - Observable that emits the underlying map object whenever the style is changed (e.g. to/from
     *  satellite). Waits to emit the first style changed event until the map is fully loaded.
     */
    get mapStyleChanged$(): Observable<MapboxMap> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapStyleChanged$) {
            this._mapStyleChanged$ = this.mapLoaded$.pipe(
                switchMap((mapboxMap) => {
                    // Once map is loaded, listen for style.load events (base style has changed)...
                    return fromEvent<MapEvent>(this.map, 'style.load').pipe(
                        // ...then check every 60ms for map.isStyleLoaded()...
                        switchMap((event) => {
                            return timer(0, 60).pipe(
                                filter(() => mapboxMap.isStyleLoaded()),
                                take(1),
                                // ...and emit the map.
                                map(() => event.target)
                            );
                        }),
                        // Emit map immediately, since we skipped first style.load event waiting for map to load.
                        startWith(mapboxMap)
                    );
                })
            );
        }

        return this._mapStyleChanged$;
    }

    get mapZoomEnd$(): Observable<MapEvent> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapZoomEnd$) {
            this._mapZoomEnd$ = fromEvent(this.map, 'zoomend');
        }

        return this._mapZoomEnd$;
    }

    get mapZoomStart$(): Observable<MapEvent> {
        if (this.isDestroyed) {
            return EMPTY;
        }

        if (!this._mapZoomStart$) {
            this._mapZoomStart$ = fromEvent(this.map, 'zoomstart');
        }

        return this._mapZoomStart$;
    }
    // #endregion

    destroy(): void {
        this.isDestroyed = true;
        this.subjects.forEach((subject) => {
            subject.complete();
        });
        this.map.remove();
    }

    setMapBounds(bounds: GeoBounds): void {
        this._requestedBoundsSubject.next(bounds);
    }

    setMapCenter(center?: GeoCoordinate): void {
        this._requestedCenterSubject.next(center);
    }

    /**
     * @description - Call this method when finished loading sources for this map.
     * This will trigger the mapSourcesLoaded$ observable.
     */
    setMapSourcesLoaded(): void {
        this._mapSourcesLoadedSubject.next(undefined);
        this._mapSourcesLoadedSubject.next(this.map);
    }

    setZoomOverride(zoomOverride: number | undefined): void {
        this._zoomOverrideSubject.next(zoomOverride);
    }

    triggerMapItemClick(itemType: MapItemType, id?: string): void {
        this._mapItemClickSubject.next({ id, itemType });
    }

    triggerMapItemHover(itemType: MapItemType, id?: string): void {
        this._mapItemHoverSubject.next({ id, itemType });
    }

    private getExtentsFromMap(mapboxMap: MapboxMap): {
        bounds: GeoBounds;
        center: GeoCoordinate;
        zoom: number;
    } {
        return {
            bounds: GeoBounds.fromMapboxLngLatBounds(
                mapboxMap.getBounds() as LngLatBounds
            ),
            center: GeoCoordinate.fromMapboxLngLat(mapboxMap.getCenter()),
            zoom: mapboxMap.getZoom(),
        };
    }
}
